import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/common/DashboardLayout';
import * as affiliateService from '../../services/affiliate.service';

/**
 * Self-service view for an affiliate/royalty partner to see their own
 * referrals and payouts. Linked from the main nav as "My Payouts", but
 * only for members whose account is actually linked to a partner record.
 *
 * Jenn, 2026-09-11: built directly from CB's own approved mockup
 * (365reset-affiliate-dashboard-mockup.html) — same sections, same visual
 * language, not a reinterpretation. Two real differences from that file,
 * both by Jenn's explicit instruction: the $1 offer is a separate CODE
 * entered at checkout, not a link baked into their referral link; and the
 * "connect your social accounts" row is marked in development rather than
 * wired to working buttons, since no platform OAuth exists yet.
 *
 * What's deliberately NOT here yet, because the real data behind it
 * doesn't exist: a real personalized landing page and real click/view
 * tracking. The prospects section keeps CB's structure but shows an
 * honest "not tracked yet" state instead of fabricated numbers.
 */
export default function AffiliateDashboard() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    affiliateService.getMyDashboard()
      .then(setData)
      .catch(err => setError(err.response?.data?.error || 'Could not load your dashboard.'));
  }, []);

  if (error) {
    return (
      <DashboardLayout>
        <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-sm px-5 py-8 text-center text-sm text-gray-500">
          {error}
        </div>
      </DashboardLayout>
    );
  }

  if (!data) {
    return (
      <DashboardLayout>
        <div className="max-w-4xl mx-auto text-sm text-gray-400">Loading…</div>
      </DashboardLayout>
    );
  }

  const { dashboards } = data;

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto">
        {dashboards.map((d, i) => (
          <PartnerSection key={d.partner.id} {...d} isLast={i === dashboards.length - 1} />
        ))}
      </div>
    </DashboardLayout>
  );
}

/** Glues the last word of a phrase to the one before it with a
 * non-breaking space, so short trailing bits like "$1" never get orphaned
 * alone on their own line regardless of container width — Jenn, 2026-09-11:
 * flagged this as a "bad split" on the $1 offer headline. */
function glueLastWord(text) {
  const parts = text.split(' ');
  if (parts.length < 2) return text;
  const last = parts.pop();
  return parts.join(' ') + String.fromCharCode(160) + last;
}

function Kicker({ children, className = '' }) {
  return <p className={`text-xs font-bold uppercase tracking-widest text-reset-terracotta mb-2 ${className}`}>{children}</p>;
}

function CopyField({ value, buttonLabel = 'Copy', dark = false }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard unavailable — value is still selectable */ }
  };
  return (
    <div className="flex items-center gap-2">
      <input readOnly value={value} onFocus={e => e.target.select()}
        className={`flex-1 min-w-0 rounded-full px-4 py-2.5 text-sm font-mono ${
          dark ? 'border border-white/20 text-reset-cream bg-white/10' : 'border border-black/10 text-reset-brown bg-white'
        }`} />
      <button onClick={handleCopy} className="px-5 py-2.5 bg-reset-terracotta text-white text-sm font-bold rounded-full hover:bg-reset-brown transition-colors shrink-0">
        {copied ? 'Copied!' : buttonLabel}
      </button>
    </div>
  );
}

function StatCard({ label, value, children, dark = false }) {
  return (
    <div className={`rounded-2xl p-6 shadow-sm ${dark ? 'bg-gradient-to-br from-reset-sage to-[#3b5245] text-reset-cream' : 'bg-white'}`}>
      <p className={`text-xs font-bold mb-2 ${dark ? 'text-reset-gold' : 'text-reset-sage'}`}>{label}</p>
      <p className={`font-serif text-3xl leading-none ${dark ? 'text-reset-cream' : 'text-reset-brownDeep'}`}>{value}</p>
      {children && <p className={`text-sm mt-2 ${dark ? 'text-reset-cream/75' : 'text-gray-500'}`}>{children}</p>}
    </div>
  );
}

const PLATFORMS = ['Instagram', 'Facebook', 'LinkedIn', 'TikTok', 'Pinterest'];

/** Not wired to anything real yet — no platform OAuth exists. Visually
 * present (matches CB's mockup), but non-functional and disabled so it
 * doesn't look like it should do something it can't. "In development" is
 * on its own line, next to the label, so it can never wrap out of view —
 * Jenn, 2026-09-11: it went missing from view once already because it
 * could wrap below the button row depending on window width. */
function ConnectAccountsRow() {
  return (
    <div>
      <div className="flex items-center gap-2 flex-wrap mb-3">
        <span className="text-sm text-gray-500">Connect your accounts to share in one tap:</span>
        <span className="text-[11px] font-bold uppercase tracking-wide text-gray-400 bg-gray-100 px-2.5 py-1 rounded-full">In development</span>
      </div>
      <div className="flex gap-2 flex-wrap">
        {PLATFORMS.map(p => (
          <button key={p} disabled title="Coming soon"
            className="px-4 py-1.5 rounded-full text-xs font-bold border border-black/10 text-gray-400 bg-white cursor-not-allowed">
            + {p}
          </button>
        ))}
      </div>
    </div>
  );
}

/** Static for now, ported from CB's approved copy — real, 365reset-specific
 * text, not filler. Not yet admin-editable; that's the next real gap to close. */
const CHANNEL_CONTENT = {
  Instagram: [
    `Some women aren't trying to do more. They're trying to live better…`,
    `There comes a point where trying harder stops being the answer…`,
    `If you're done following everyone else's rules for how life should work…`,
  ],
  Facebook: [
    `A lot of capable women are exhausted. Have you been single-handedly holding everything together for too long? 365reset helps you change how you think and live, so life takes less effort and gives more back.`,
    `Many capable women have had to step back, even shrink, to keep the peace. If you're ready to live differently now, you're not alone.`,
  ],
  LinkedIn: [
    `Capability shouldn't cost you your peace. 365reset is a mentored membership for women who want to make confident decisions and use their time and energy with more intention — without overhauling their lives to do it.`,
  ],
  TikTok: [
    `Hook: "Your calendar shouldn't control you." Shift: "Structure should support you — not leash you." CTA: "Are you ready to use your week differently?"`,
    `Hook: "Busy isn't the same as moving forward." Shift: "Fear keeps you in motion. Purpose moves you ahead." CTA: "Are you ready to catapult forward?"`,
  ],
  Pinterest: [
    `Pin description: "A grounded, mentored membership for women ready to live with more intention." Destination: your link, set automatically.`,
  ],
};

const EMAIL_TEXT = `Subject: A friend thought you'd love this\n\nHi — I wanted to pass along something that's made a real difference for me. It's called 365reset, a mentored membership for women who want their life to feel a little more workable. If you're curious, I'd love for you to take a look.`;
const NEWSLETTER_TEXT = `365reset is a mentored membership that helps women make confident decisions and use their time and energy with intention — without adding one more thing to their plate. Worth a look if that resonates.`;

function CopyButton({ text, label = 'Copy' }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch {}
  };
  return (
    <button onClick={handleCopy} className="px-4 py-1.5 text-xs font-bold rounded-full bg-reset-terracotta text-white hover:bg-reset-brown transition-colors">
      {copied ? 'Copied!' : label}
    </button>
  );
}

function ShareTabs({ link }) {
  const [active, setActive] = useState('Instagram');
  const posts = CHANNEL_CONTENT[active];

  return (
    <div>
      <div className="flex gap-2 flex-wrap mb-4">
        {PLATFORMS.map(p => (
          <button key={p} onClick={() => setActive(p)}
            className={`px-4 py-2 rounded-full text-sm font-bold transition-colors ${
              active === p ? 'bg-reset-terracotta text-white' : 'bg-reset-creamDark text-reset-brown hover:bg-reset-gold/30'
            }`}>
            {p}
          </button>
        ))}
      </div>
      {/* items-stretch (grid default) equalizes card height per row; flex
         flex-col + mt-auto on the button pins it to the bottom of each
         card regardless of caption length, so buttons stay level across
         cards instead of floating at different heights — Jenn, 2026-09-11:
         flagged misaligned "bubbles" across cards of different lengths. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        {posts.map((body, i) => (
          <div key={i} className="bg-white rounded-2xl shadow-sm p-4 flex flex-col">
            <p className="text-sm text-reset-brown whitespace-pre-line leading-relaxed mb-3">{body}</p>
            <div className="mt-auto">
              <CopyButton text={`${body}\n\n${link}`} label="Copy caption" />
            </div>
          </div>
        ))}
      </div>
      <p className="text-xs text-gray-400 max-w-2xl">
        Each tab is its own set, written for how that platform gets used — a script for TikTok, a short post for LinkedIn, not the same caption everywhere.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
        <div className="bg-white rounded-2xl shadow-sm p-5 flex flex-col">
          <p className="text-xs font-bold text-reset-sage mb-2">Email you can send</p>
          <p className="text-sm text-reset-brown whitespace-pre-line leading-relaxed mb-3">{EMAIL_TEXT}</p>
          <div className="mt-auto"><CopyButton text={`${EMAIL_TEXT}\n\n${link}`} label="Copy email" /></div>
        </div>
        <div className="bg-white rounded-2xl shadow-sm p-5 flex flex-col">
          <p className="text-xs font-bold text-reset-sage mb-2">Newsletter insert</p>
          <p className="text-sm text-reset-brown whitespace-pre-line leading-relaxed mb-3">{NEWSLETTER_TEXT}</p>
          <div className="mt-auto"><CopyButton text={`${NEWSLETTER_TEXT}\n\n${link}`} label="Copy insert" /></div>
        </div>
      </div>
    </div>
  );
}

/** A member can hold both an affiliate and a royalty role at once (e.g.
 * Patti, Trish) — each gets its own section rather than one role silently
 * hiding the other. Exported so AdminAffiliatePreview.jsx can render the
 * exact same real dashboard for Team365, not a second, divergent copy. */
export function PartnerSection({ partner, trial_code, owed, paid, commissions, payouts, referral_count, prospects, referred_members, media, demo_account, isLast }) {
  const isAffiliate = partner.partner_type === 'affiliate';
  // Jenn, 2026-09-11: "the dashboard framing needs to show" — regardless of
  // profile/approval status. The referral code exists from the moment a
  // partner record is created, so the link itself is always real; what's
  // NOT real yet pre-approval is attribution actually working and the
  // personalized landing page rendering. Those get an honest "not live
  // yet" treatment inline, but the full CB layout always shows.
  const link = (isAffiliate && partner.referral_code) ? `${window.location.origin}/join?ref=${encodeURIComponent(partner.referral_code)}` : null;
  // Once approved, the real live page is the same content — link there
  // directly. Before approval, the same page is only reachable through the
  // no-login preview link (see affiliate.controller.js's buildPreviewUrls),
  // so they can still see it and tell Jenn about changes while it's waiting
  // on Team365 — Jenn, 2026-09-11: "make sure...the affiliate can see the
  // view of the landing page and the confirmation page."
  const landingPageUrl = (isAffiliate && partner.profile_complete && partner.landing_page_slug)
    ? `${window.location.origin}/a/${partner.landing_page_slug}`
    : partner.landing_page_preview_url || null;
  const firstName = (partner.name || '').split(' ')[0];

  return (
    <div className={isLast ? '' : 'mb-16 pb-16 border-b border-gray-100'}>
      {/* ---------- welcome ---------- */}
      <div className="mb-6">
        <Kicker>Welcome back</Kicker>
        <h1 className="font-serif text-3xl sm:text-4xl font-semibold text-reset-brownDeep leading-tight max-w-xl">
          Everything you need to share 365reset — and see exactly <em className="italic text-reset-terracotta">where it stands</em>.
        </h1>
      </div>

      {/* ---------- status note — informational only, never hides the dashboard
           below it. Jenn, 2026-09-11: "It doesn't matter if her profile was
           filled out or not. The dashboard framing needs to show." The full
           CB layout always renders now; this is just an honest heads-up
           about what's still activating. ---------- */}
      {isAffiliate && !partner.profile_complete && !partner.awaiting_approval && (
        <div className="rounded-2xl bg-reset-creamDark border-l-[3px] border-reset-terracotta p-7 mb-8">
          <Kicker>Almost there</Kicker>
          <p className="text-sm text-reset-brown mb-4 max-w-md">
            {partner.waiting_for_videos
              /* Jenn, 2026-10-07: say plainly what we're still waiting for. */
              ? "Thank you for your photo and story. We're still waiting for your two videos. Once they're in, Jenn's team will look at your page, and then your link and $1 offer turn on."
              : "Your link and $1 offer are ready below — they'll start earning credit once your profile is finished and Jenn's team gives it a look."}
          </p>
          {partner.intake_url && (
            <a href={partner.intake_url} className="inline-block px-5 py-2.5 bg-reset-terracotta text-white text-sm font-semibold rounded-full hover:bg-reset-brown transition-colors">
              {partner.waiting_for_videos ? 'Add your videos →' : 'Finish your profile →'}
            </a>
          )}
        </div>
      )}

      {isAffiliate && partner.awaiting_approval && (
        <div className="rounded-2xl bg-reset-creamDark border-l-[3px] border-reset-sage p-7 mb-8">
          <Kicker className="text-reset-sage">In review</Kicker>
          <p className="text-sm text-reset-brown max-w-md">
            You're all set on your end — Jenn's team is giving your page a look before it goes live. Your link and $1 offer will turn on as soon as they approve it, no action needed from you.
          </p>
        </div>
      )}

      {/* ---------- link card — gold/cream gradient, matching CB's approved
           file. Always shows once a partner exists (the code itself is real
           from day one); only the "live" state of the button/preview
           changes based on profile + approval. ---------- */}
      {link && (
        <div className="rounded-2xl p-7 mb-8 shadow-sm bg-gradient-to-br from-reset-gold/35 via-reset-creamDark to-reset-creamDark grid grid-cols-1 md:grid-cols-[1.3fr_1fr] gap-7 items-center">
          <div>
            <Kicker className="text-reset-sage">Your link</Kicker>
            <CopyField value={link} buttonLabel="Copy link" />
            <p className="text-sm text-gray-500 mt-3 mb-4 max-w-md">
              This link is yours alone. <strong className="text-reset-sage">Anyone who clicks it is tagged to you for 60 days</strong> — even if they come back and join weeks later, the credit and commission are still yours.
              {!partner.profile_complete && ' It\'ll start earning credit once your profile is finished and reviewed — copy it now so it\'s ready.'}
            </p>
            {landingPageUrl ? (
              <a href={landingPageUrl} target="_blank" rel="noopener noreferrer"
                className="inline-block px-5 py-2.5 border-[1.5px] border-reset-brownDeep text-reset-brownDeep text-sm font-bold rounded-full hover:bg-reset-brownDeep hover:text-reset-cream transition-colors">
                View your landing page
              </a>
            ) : (
              <span className="inline-block px-5 py-2.5 border-[1.5px] border-gray-300 text-gray-400 text-sm font-bold rounded-full cursor-default">
                Landing page — coming once your profile's in
              </span>
            )}
          </div>
          <div className="bg-reset-cream rounded-xl p-4 shadow-sm">
            <div className="aspect-video rounded-lg border border-dashed border-reset-terracotta/40 bg-gradient-to-br from-reset-terracotta/10 to-reset-sage/10 flex items-center justify-center text-center px-4">
              <p className="font-serif italic text-reset-terracotta text-sm">
                {landingPageUrl ? `${firstName}'s 365reset page` : 'Your personalized page — coming soon'}
              </p>
            </div>
            <div className="flex items-center justify-between mt-2">
              <p className="text-xs text-gray-500">Built from your photo &amp; story, once it's ready.</p>
              {landingPageUrl && (
                <a href={landingPageUrl} target="_blank" rel="noopener noreferrer" className="text-xs font-bold text-reset-terracotta shrink-0 ml-2">Preview →</a>
              )}
            </div>
            {partner.confirmation_page_preview_url && (
              <p className="text-xs text-gray-500 mt-2">
                <a href={partner.confirmation_page_preview_url} target="_blank" rel="noopener noreferrer" className="font-bold text-reset-terracotta">
                  Preview your welcome page →
                </a> — what new members see the moment they join through you.
              </p>
            )}
          </div>
        </div>
      )}

      {/* ---------- stat strip — real zeros are still real numbers, always shown ---------- */}
      {link && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <StatCard label="Referrals" value={referral_count ?? 0}>People who joined through your link.</StatCard>
          <StatCard label="Commissions" value={`$${Number(owed).toFixed(2)}`}>Owed to you right now.</StatCard>
          <StatCard label="Paid to date" value={`$${Number(paid).toFixed(2)}`} dark>Your total payouts so far.</StatCard>
        </div>
      )}

      {/* ---------- the $1 offer — always shows the real card; the code
           itself only exists once intake is submitted (it's generated off
           the real referral code at that point), so pre-submission this is
           an honest "coming" state in the same visual frame, not hidden. ---------- */}
      {link && (
        <div className="rounded-2xl bg-reset-brownDeep p-7 mb-8 shadow-sm">
          <span className="inline-block text-xs font-bold text-reset-brownDeep bg-reset-gold px-3 py-1 rounded-full mb-3">Only affiliates can offer this</span>
          {trial_code ? (
            <>
              <h3 className="font-serif text-2xl text-reset-cream mb-2 max-w-sm">Give the people you refer {glueLastWord(trial_code.label)}.</h3>
              <p className="text-sm text-reset-cream/70 mb-4 max-w-md">
                This isn't on the public site — it's yours to extend. They enter this code themselves at checkout; it's a separate thing from your link, not baked into it.
              </p>
              <CopyField value={trial_code.code} buttonLabel="Copy offer code" dark />
            </>
          ) : (
            <>
              <h3 className="font-serif text-2xl text-reset-cream mb-2 max-w-sm">Give the people you refer 10 days for $1.</h3>
              <p className="text-sm text-reset-cream/70 max-w-md">
                Your own offer code gets created the moment your profile is submitted — check back here once it's in.
              </p>
            </>
          )}
        </div>
      )}

      {/* ---------- demo account — tour prospects through the real thing ---------- */}
      {link && demo_account && (
        <div className="mb-8">
          <Kicker>Tour prospects through the membership</Kicker>
          <p className="text-sm text-gray-500 mb-3">Your own demo login — for you to show them around, not to hand over.</p>
          <DemoAccountCard demo_account={demo_account} />
        </div>
      )}

      {/* ---------- share — none of this ever depended on the affiliate's own
           photo/story, so it's always been safe to show; gating it behind
           profile completion was a real mistake, not intentional. ---------- */}
      {link && (
        <div className="mb-8">
          <Kicker>Share 365reset</Kicker>
          <div className="bg-white rounded-2xl shadow-sm px-5 py-4 mb-4">
            <ConnectAccountsRow />
          </div>
          <ShareTabs link={link} />
          {media?.length > 0 && <ShareableImages media={media} />}
        </div>
      )}

      {/* ---------- your members ---------- */}
      {isAffiliate && referred_members != null && (
        <div className="mb-8">
          <Kicker>Your members</Kicker>
          <p className="text-sm text-gray-500 mb-3">Everyone who's joined through your link, and where they stand.</p>
          {referred_members.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-sm px-5 py-6 text-center text-sm text-gray-400">
              No one yet — referrals will show up here as soon as your link brings in a real member.
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden overflow-x-auto">
              <table className="w-full text-sm min-w-[480px]">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="text-left font-bold text-gray-400 text-xs uppercase tracking-wide px-5 py-3">Name</th>
                    <th className="text-left font-bold text-gray-400 text-xs uppercase tracking-wide px-5 py-3">Joined via your link</th>
                    <th className="text-left font-bold text-gray-400 text-xs uppercase tracking-wide px-5 py-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {referred_members.map(m => (
                    <tr key={m.id} className="border-b border-gray-50 last:border-0">
                      <td className="px-5 py-3 text-reset-brown font-medium">{m.name}</td>
                      <td className="px-5 py-3 text-gray-500">{m.member_since ? new Date(m.member_since).toLocaleDateString() : '—'}</td>
                      <td className="px-5 py-3">
                        <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full ${
                          m.status === 'active' ? 'bg-reset-sage/15 text-reset-sage' : 'bg-reset-gold/25 text-reset-brown'
                        }`}>
                          {m.status_label}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="text-xs text-gray-400 bg-reset-creamDark px-5 py-3">
                We don't show individual dollar amounts here — that's between you and Jenn, in your commissions and payout history below.
              </p>
            </div>
          )}
        </div>
      )}

      {/* ---------- prospects — structure matches the mockup. 2026-09-28:
           real counts from our own first-party tracking (marketing_events),
           each person counted once, since tracking started. Falls back to
           dashes if the numbers ever fail to arrive — never invented. ---------- */}
      {isAffiliate && link && (
        <div className="mb-8">
          <Kicker>Your prospects</Kicker>
          <p className="text-sm text-gray-500 mb-3">Everyone who's clicked or looked, but hasn't joined yet.</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
            {[
              ['Clicked your link', prospects?.clicked],
              ['Viewed your landing page', prospects?.viewedLanding],
              ['Started signing up', prospects?.startedSignup],
              ['Joined', prospects ? (referral_count ?? 0) : null],
            ].map(([label, value], i) => (
              <div key={label} className={`rounded-2xl p-5 text-center shadow-sm flex flex-col ${i === 3 ? 'bg-reset-sage text-reset-cream' : 'bg-white'}`}>
                <p className={`font-serif text-2xl ${i === 3 ? 'text-reset-cream' : 'text-reset-brownDeep'}`}>{value ?? '—'}</p>
                <p className={`text-xs mt-1 ${i === 3 ? 'text-reset-cream/80' : 'text-gray-500'}`}>{label}</p>
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-500 text-center">
            Counted since September 28, 2026, when we started tracking. Each person is counted once, however many times they visit. "Joined" counts everyone who has ever joined through your link.
          </p>
        </div>
      )}

      {/* ---------- commissions ---------- */}
      <div className="mb-8">
        <Kicker>Recent commissions</Kicker>
        {commissions.length === 0 ? (
          <p className="text-sm text-gray-400">No commissions yet.</p>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden divide-y divide-gray-50">
            {commissions.map(c => (
              <div key={c.id} className="px-5 py-3 flex items-center justify-between text-sm">
                <span className="text-gray-500">{new Date(c.earned_at).toLocaleDateString()}</span>
                <span className="text-reset-brown font-medium">${Number(c.commission_amount).toFixed(2)}</span>
                <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full ${
                  c.status === 'paid' ? 'bg-reset-sage/15 text-reset-sage' : 'bg-reset-gold/25 text-reset-brown'
                }`}>
                  {c.status}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ---------- payout history ---------- */}
      <div>
        <Kicker>Payout history</Kicker>
        {payouts.length === 0 ? (
          <p className="text-sm text-gray-400">No payouts recorded yet.</p>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden divide-y divide-gray-50">
            {payouts.map(p => (
              <div key={p.id} className="px-5 py-4 flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <p className="text-sm text-reset-brown">{new Date(p.payout_date).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                  {p.note && <p className="text-xs text-gray-400 mt-0.5">{p.note}</p>}
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-reset-sage/15 text-reset-sage">Paid</span>
                  <span className="font-serif text-lg text-reset-brownDeep">${Number(p.amount).toFixed(2)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {isLast && (
        <div className="text-center pt-14">
          <p className="font-serif italic text-xl text-reset-terracotta mb-1">Thank you for sharing this with the people you love.</p>
          <p className="text-sm text-gray-500">Questions about anything here? <a href="/dashboard" className="text-reset-sage font-bold">Message Jenn</a></p>
        </div>
      )}
    </div>
  );
}

function ShareableImages({ media }) {
  return (
    <div className="mt-4">
      <p className="text-xs font-bold text-reset-sage mb-3">Ready-to-share images — save one, then post or send it like any other photo</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {media.map(m => (
          <a key={m.id} href={m.file_url} target="_blank" rel="noopener noreferrer"
            className="group block rounded-xl overflow-hidden border border-black/5 hover:border-reset-terracotta transition-colors bg-white shadow-sm">
            <div className="aspect-square bg-reset-cream overflow-hidden">
              <img src={m.file_url} alt={m.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
            </div>
            <p className="text-xs text-gray-500 px-2 py-1.5 truncate group-hover:text-reset-terracotta">{m.title}</p>
          </a>
        ))}
      </div>
    </div>
  );
}

/**
 * A real demo login to show prospects — Patti, 2026-09-06: "a space on
 * their personal profile's affiliate page where this login and its
 * password can be retrieved at any time." Password is hidden by default,
 * click to reveal — it's real and sensitive-ish, not something that
 * should just sit exposed on page load.
 */
function DemoAccountCard({ demo_account }) {
  const [showPassword, setShowPassword] = useState(false);

  return (
    <div className="bg-white rounded-2xl shadow-sm px-6 py-2 divide-y divide-reset-creamDark">
      <div className="flex items-center justify-between py-4">
        <div>
          <p className="text-xs font-bold text-reset-sage mb-0.5">Email</p>
          <p className="text-sm font-mono text-reset-brown">{demo_account.email}</p>
        </div>
        <CopyButton text={demo_account.email} />
      </div>
      <div className="flex items-center justify-between py-4">
        <div>
          <p className="text-xs font-bold text-reset-sage mb-0.5">Password</p>
          {showPassword ? (
            <p className="text-sm font-mono text-reset-brown">{demo_account.password}</p>
          ) : (
            <button onClick={() => setShowPassword(true)} className="text-sm text-reset-terracotta hover:underline">Show</button>
          )}
        </div>
        {showPassword && <CopyButton text={demo_account.password} />}
      </div>
      <p className="text-xs text-gray-400 py-3">Log in yourself and walk a prospect through it — on a call, a screen-share, or side by side. This login is yours alone.</p>
    </div>
  );
}
